import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
import useAutoTranslate from '../hooks/useAutoTranslate'
import {
    ATTACK_SETUP_STORAGE_KEY,
    ATTACK_TEMPLATE_FIELDS,
    getAttackTemplateFieldById,
    normalizeAttackSetupConfig,
    syncAttackTemplateSelection
} from './attackTemplateAssignments'
import { buildSavedAttackTemplatesUrl } from './attackTemplatePlatform'

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

const sortTemplatesByUpdatedAt = templates =>
    [...(Array.isArray(templates) ? templates : [])].sort((left, right) =>
        Number(right?.updatedAt ?? 0) - Number(left?.updatedAt ?? 0) ||
        String(left?.name ?? '').localeCompare(String(right?.name ?? ''))
    )

function TemplatePickerDialog({
    open,
    title,
    templates,
    loading,
    error,
    onClose,
    onReload,
    onSelect,
    appLanguage = 'en'
}) {
    const [searchTerm, setSearchTerm] = React.useState('')
    const deferredSearchTerm = React.useDeferredValue(searchTerm)
    const t = useAutoTranslate(appLanguage, [
        'Select Attack Template',
        'Search by name or user',
        'No saved attack templates yet.',
        'Create templates in Attack Templates first, then select them here.',
        'Could not load saved attack templates.',
        'Retry',
        'Saved by',
        'No description'
    ])

    React.useEffect(() => {
        if (open)
            setSearchTerm('')
    }, [open])

    const filteredTemplates = React.useMemo(() => {
        const normalizedQuery = String(deferredSearchTerm ?? '').trim().toLowerCase()
        if (!normalizedQuery)
            return templates

        return templates.filter(template => [
            template?.name,
            template?.description,
            template?.ownerUsername
        ].join(' ').toLowerCase().includes(normalizedQuery))
    }, [deferredSearchTerm, templates])

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: 'min(calc(100vw - 40px), 720px)',
                    maxWidth: '720px',
                    borderRadius: '16px',
                    background: 'linear-gradient(180deg, rgba(31,44,70,0.98) 0%, rgba(24,36,59,0.98) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)'
                }
            }}
            BackdropProps={{
                sx: {
                    backdropFilter: 'blur(10px)',
                    backgroundColor: 'rgba(4, 10, 20, 0.62)'
                }
            }}
        >
            <DialogTitle sx={{ px: 2.5, pt: 2.2, pb: 1.3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#f3f7ff' }}>
                        {title || t('Select Attack Template')}
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: '#a9b4ca' }}>
                        <CloseRoundedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ px: 2.5, pb: 2.5 }}>
                <TextField
                    fullWidth
                    placeholder={t('Search by name or user')}
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    sx={{
                        mb: 2,
                        '& .MuiOutlinedInput-root': {
                            minHeight: 44,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(17, 26, 44, 0.92)'
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ color: '#a9b4ca' }} />
                            </InputAdornment>
                        )
                    }}
                />

                {error && (
                    <Alert
                        severity="error"
                        action={
                            <Button color="inherit" size="small" onClick={onReload}>
                                {t('Retry')}
                            </Button>
                        }
                        sx={{ mb: 2, borderRadius: 2 }}
                    >
                        {t('Could not load saved attack templates.')}
                    </Alert>
                )}

                <Box
                    sx={{
                        maxHeight: 'min(52vh, 520px)',
                        overflowY: 'auto',
                        pr: 0.5
                    }}
                >
                    {loading ? (
                        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
                            <CircularProgress />
                        </Stack>
                    ) : filteredTemplates.length > 0 ? (
                        <Stack spacing={1.3}>
                            {filteredTemplates.map(template => (
                                <Box
                                    key={template.id}
                                    onClick={() => onSelect(template)}
                                    sx={{
                                        p: 1.7,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        cursor: 'pointer',
                                        transition: 'background-color 160ms ease, border-color 160ms ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            borderColor: 'rgba(94, 157, 255, 0.36)'
                                        }
                                    }}
                                >
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f2f6ff' }}>
                                        {template?.name || 'Untitled'}
                                    </Typography>
                                    <Typography sx={{ mt: 0.65, color: '#a8b4cc', fontSize: '0.94rem' }}>
                                        {template?.description || t('No description')}
                                    </Typography>
                                    <Stack direction="row" spacing={0.65} alignItems="center" sx={{ mt: 1.1 }}>
                                        <PersonOutlineRoundedIcon sx={{ fontSize: 15, color: '#94a4c3' }} />
                                        <Typography sx={{ color: '#94a4c3', fontSize: '0.9rem' }}>
                                            {`${t('Saved by')} ${template?.ownerUsername || 'Unknown'}`}
                                        </Typography>
                                    </Stack>
                                </Box>
                            ))}
                        </Stack>
                    ) : (
                        <Box
                            sx={{
                                p: 2.2,
                                borderRadius: '12px',
                                border: '1px solid rgba(255,255,255,0.08)',
                                backgroundColor: 'rgba(255,255,255,0.03)'
                            }}
                        >
                            <Typography sx={{ fontWeight: 700, color: '#f2f6ff' }}>
                                {t('No saved attack templates yet.')}
                            </Typography>
                            <Typography sx={{ mt: 0.75, color: '#a8b4cc' }}>
                                {t('Create templates in Attack Templates first, then select them here.')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
        </Dialog>
    )
}

function AttackTemplateField({ field, assignment, onOpenPicker, appLanguage = 'en' }) {
    const t = useAutoTranslate(appLanguage, [
        field.label,
        field.helper,
        field.placeholder
    ])

    const displayValue = assignment?.name || t(field.placeholder)

    return (
        <Box>
            <Typography sx={{ mb: 0.65, fontSize: '0.98rem', fontWeight: 700, color: '#f1f5ff' }}>
                {t(field.label)}
            </Typography>

            <TextField
                fullWidth
                value={displayValue}
                onClick={onOpenPicker}
                inputProps={{ readOnly: true }}
                sx={{
                    '& .MuiOutlinedInput-root': {
                        minHeight: 36,
                        borderRadius: '10px',
                        backgroundColor: 'rgba(17, 26, 44, 0.66)',
                        pr: 0.5,
                        cursor: 'pointer'
                    },
                    '& .MuiInputBase-input': {
                        color: assignment?.name ? '#f2f6ff' : '#9fb0cd',
                        fontWeight: assignment?.name ? 600 : 500,
                        fontSize: '0.92rem',
                        cursor: 'pointer'
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Box
                                component="button"
                                type="button"
                                onClick={event => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    onOpenPicker()
                                }}
                                sx={{
                                    width: 40,
                                    height: 34,
                                    borderRadius: '10px',
                                    display: 'grid',
                                    placeItems: 'center',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    backgroundColor: 'rgba(255,255,255,0.03)',
                                    color: '#eff4ff',
                                    cursor: 'pointer',
                                    p: 0,
                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.06)'
                                    }
                                }}
                            >
                                <SearchRoundedIcon sx={{ fontSize: 22 }} />
                            </Box>
                        </InputAdornment>
                    )
                }}
            />

            {assignment?.ownerUsername ? (
                <Stack direction="row" spacing={0.65} alignItems="center" sx={{ mt: 0.75 }}>
                    <PersonOutlineRoundedIcon sx={{ fontSize: 14, color: '#a5b2cb' }} />
                    <Typography sx={{ color: '#a5b2cb', fontSize: '0.88rem' }}>
                        {assignment.ownerUsername}
                    </Typography>
                </Stack>
            ) : (
                <Box sx={{ height: 20, mt: 0.35 }} />
            )}

            <Stack direction="row" spacing={0.65} alignItems="flex-start" sx={{ mt: 0.15 }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: '#a5b2cb', mt: '2px' }} />
                <Typography sx={{ color: '#a5b2cb', fontSize: '0.88rem', lineHeight: 1.45 }}>
                    {t(field.helper)}
                </Typography>
            </Stack>
        </Box>
    )
}

export default function AttackSetupPanel({ userPlugins, onChange, appLanguage = 'en', templatePlatform = 'gge' }) {
    const [templates, setTemplates] = React.useState([])
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState(false)
    const [pickerFieldId, setPickerFieldId] = React.useState('')
    const t = useAutoTranslate(appLanguage, [
        'Correct Attack Setups',
        'Bot tries to recalculate the maximum amount to prevent errors',
        'Select Attack Template'
    ])
    const attackSetupConfig = React.useMemo(
        () => normalizeAttackSetupConfig(userPlugins?.[ATTACK_SETUP_STORAGE_KEY]),
        [userPlugins]
    )

    const loadTemplates = React.useCallback(async () => {
        try {
            setLoading(true)
            setError(false)

            const response = await fetch(buildSavedAttackTemplatesUrl(templatePlatform), {
                credentials: 'same-origin'
            })

            if (!response.ok)
                throw new Error(`Template list request failed with ${response.status}`)

            const payload = await response.json()
            setTemplates(sortTemplatesByUpdatedAt(payload?.templates))
        }
        catch (loadError) {
            console.warn('Failed to load saved attack templates for attack setup', loadError)
            setTemplates([])
            setError(true)
        }
        finally {
            setLoading(false)
        }
    }, [templatePlatform])

    React.useEffect(() => {
        loadTemplates()
    }, [loadTemplates])

    const updateAttackSetup = updater => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        const currentConfig = normalizeAttackSetupConfig(nextPlugins?.[ATTACK_SETUP_STORAGE_KEY])
        const nextConfig = typeof updater === 'function' ? updater(currentConfig) : updater
        nextPlugins[ATTACK_SETUP_STORAGE_KEY] = nextConfig
        onChange(nextPlugins)
    }

    const handleCorrectAttackSetupsChange = nextValue => {
        updateAttackSetup(currentConfig => ({
            ...currentConfig,
            correctAttackSetups: Boolean(nextValue)
        }))
    }

    const handleTemplateSelect = template => {
        const field = getAttackTemplateFieldById(pickerFieldId)
        if (!field || !template)
            return

        const nextPlugins = cloneValue(userPlugins ?? {})
        syncAttackTemplateSelection(nextPlugins, field.id, template)
        onChange(nextPlugins)
        setPickerFieldId('')
    }

    const activeField = ATTACK_TEMPLATE_FIELDS.find(field => field.id === pickerFieldId) ?? null

    return (
        <>
            <Grid container spacing={2.2}>
                <Grid item xs={12} md={6}>
                    <Box sx={{ pt: 0.35 }}>
                        <Stack direction="row" alignItems="center" spacing={1.1}>
                            <Switch
                                checked={attackSetupConfig.correctAttackSetups}
                                onChange={event => handleCorrectAttackSetupsChange(event.target.checked)}
                            />
                            <Typography sx={{ fontWeight: 700, color: '#f2f6ff', fontSize: '1rem' }}>
                                {t('Correct Attack Setups')}
                            </Typography>
                        </Stack>
                        <Stack direction="row" spacing={0.65} alignItems="flex-start" sx={{ mt: 0.6 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: '#a5b2cb', mt: '2px' }} />
                            <Typography sx={{ color: '#a5b2cb', fontSize: '0.9rem', lineHeight: 1.45 }}>
                                {t('Bot tries to recalculate the maximum amount to prevent errors')}
                            </Typography>
                        </Stack>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <AttackTemplateField
                        field={ATTACK_TEMPLATE_FIELDS[0]}
                        assignment={attackSetupConfig.assignments.pointsOuters}
                        onOpenPicker={() => setPickerFieldId('pointsOuters')}
                        appLanguage={appLanguage}
                    />
                </Grid>

                {ATTACK_TEMPLATE_FIELDS.slice(1).map(field => (
                    <Grid item xs={12} md={6} key={field.id}>
                        <AttackTemplateField
                            field={field}
                            assignment={attackSetupConfig.assignments[field.id]}
                            onOpenPicker={() => setPickerFieldId(field.id)}
                            appLanguage={appLanguage}
                        />
                    </Grid>
                ))}
            </Grid>

            <TemplatePickerDialog
                open={Boolean(activeField)}
                title={activeField ? `${t('Select Attack Template')} - ${activeField.label}` : ''}
                templates={templates}
                loading={loading}
                error={error}
                onClose={() => setPickerFieldId('')}
                onReload={loadTemplates}
                onSelect={handleTemplateSelect}
                appLanguage={appLanguage}
            />
        </>
    )
}
